import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { useAuth } from '../../context/AuthContext';
import * as dbService from '../../services/dbService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const UserSettings = () => {
  const { state } = useStory();
  const { user, updateEmail, updatePassword } = useAuth();
  const { subscriptionTier } = state;

  // User Settings State
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountMsg, setAccountMsg] = useState({ type: '', text: '' });
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);
  const [stayLoggedIn, setStayLoggedIn] = useState(false);

  useEffect(() => {
    const savedStayLoggedIn = localStorage.getItem('weaver_stay_logged_in');
    if (savedStayLoggedIn !== null) {
      setStayLoggedIn(savedStayLoggedIn === 'true');
    }
  }, []);

  const handleToggleStayLoggedIn = (e) => {
    const isChecked = e.target.checked;
    setStayLoggedIn(isChecked);
    localStorage.setItem('weaver_stay_logged_in', isChecked);
  };

  useEffect(() => {
    if (user && user.email) {
      setEmail(user.email);
      dbService.getUserProfile(user.id).then(profile => {
        if (profile) {
          setUsername(profile.username || '');
          setOriginalUsername(profile.username || '');
        }
      });
    }
  }, [user]);

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    setAccountMsg({ type: '', text: '' });
    setIsUpdatingAccount(true);

    try {
      if (username !== originalUsername) {
        await dbService.updateUsername(user.id, username);
        setOriginalUsername(username);
        setAccountMsg({ type: 'success', text: 'Username updated successfully.' });
      }

      if (email !== user.email) {
        const { error } = await updateEmail(email);
        if (error) throw error;
        setAccountMsg(prev => ({ type: 'success', text: prev.text ? prev.text + ' Email update verification sent. Please check your inbox.' : 'Email update verification sent. Please check your inbox.' }));
      }

      if (newPassword) {
        if (newPassword !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        const { error } = await updatePassword(newPassword);
        if (error) throw error;
        setAccountMsg(prev => ({ type: 'success', text: prev.text ? prev.text + ' Password updated successfully.' : 'Password updated successfully.' }));
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setAccountMsg({ type: 'error', text: err.message || 'Failed to update account.' });
    } finally {
      setIsUpdatingAccount(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-gray-100 dark:border-purple-800 pb-6 gap-4">
          <div>
            <h1 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">Account & Billing</h1>
            <p className="text-gray-900 dark:text-purple-500 mt-2">Manage your account settings and preferences.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-6">
            <div className="p-6 bg-gray-50 dark:bg-purple-900/20 rounded-2xl border border-gray-200 dark:border-purple-800">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiIcons.FiCreditCard} />
                <span>Subscription</span>
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-900 dark:text-purple-400">Current Tier</p>
                  <p className="text-lg font-bold text-amber-600">{subscriptionTier || 'Wanderer'}</p>
                </div>
                <Link to="/pricing" className="bg-gray-100 text-gray-900 dark:bg-purple-900/50 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800 font-bold rounded-lg w-full text-center text-xs py-2 mt-2 block transition-colors">
                  Manage Billing
                </Link>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 space-y-6">
            <form onSubmit={handleUpdateAccount} className="p-6 bg-white dark:bg-purple-950/50 rounded-2xl border border-gray-100 dark:border-purple-800 shadow-sm space-y-6">
              <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <SafeIcon icon={FiIcons.FiSettings} />
                <span>Account Settings</span>
              </h3>

              {accountMsg.text && (
                <div className={`p-3 rounded-lg text-sm ${accountMsg.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {accountMsg.text}
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="label-text">Username</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="input-field"
                      placeholder="Your username"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="label-text">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field"
                      placeholder="Your email address"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 dark:border-purple-800">
                  <h4 className="text-sm font-bold text-gray-900 dark:text-purple-200 mb-4">Preferences</h4>
                  <label className="flex items-center space-x-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={stayLoggedIn}
                      onChange={handleToggleStayLoggedIn}
                      className="rounded border-purple-300 text-amber-500 focus:ring-amber-500 bg-white dark:bg-purple-900"
                    />
                    <span className="label-text mb-0">Stay logged in</span>
                  </label>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-purple-200 mb-4">Change Password</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="label-text">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field"
                        placeholder="Leave blank to keep current"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="label-text">Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input-field"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button type="submit" disabled={isUpdatingAccount} className="btn-primary py-2 px-6 flex items-center space-x-2">
                  {isUpdatingAccount ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiSave} />}
                  <span>Update Account</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserSettings;
